/**
 * Ticket KN1: Serverfunktionen des Keynote-Bereichs.
 *
 * Die öffentliche Leseschicht liegt in `keynotes.server.ts` und wird hier nur
 * innerhalb der Handler geladen – ein Import auf Modulebene würde den
 * Dienstschlüssel in das Browserpaket ziehen.
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import type { KeynoteSpeaker } from "@/lib/keynotes";
import type { KeynoteSpeakerAdminRow } from "@/lib/keynotes.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const listPublicKeynoteSpeakers = createServerFn({ method: "GET" }).handler(
  async (): Promise<KeynoteSpeaker[]> => {
    const { listPublicKeynoteSpeakersOnServer } = await import("@/lib/keynotes.server");
    return listPublicKeynoteSpeakersOnServer();
  },
);

/**
 * Seitenzustand: Sichtbarkeit und Inhalt getrennt. Der Bereich kann für interne
 * Rollen offen sein, auch wenn noch keine Referenten eingetragen sind – dann
 * zeigt die Seite einen Hinweis statt einer 404.
 */
export const loadKeynotePage = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ visible: boolean; speakers: KeynoteSpeaker[]; shareImageUrl: string | null }> => {
    const { keynoteAreaVisible, listPublicKeynoteSpeakersOnServer, keynoteShareImageUrlOnServer } =
      await import("@/lib/keynotes.server");
    if (!(await keynoteAreaVisible())) return { visible: false, speakers: [], shareImageUrl: null };
    const speakers = await listPublicKeynoteSpeakersOnServer();
    const shareImageUrl = await keynoteShareImageUrlOnServer();
    return { visible: true, speakers: speakers, shareImageUrl };
  },
);

export const listKeynoteSpeakersForAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async (): Promise<KeynoteSpeakerAdminRow[]> => {
    const { listKeynoteSpeakersForAdminOnServer } = await import("@/lib/keynotes.server");
    return listKeynoteSpeakersForAdminOnServer();
  });

const speakerInputSchema = z.object({
  profileId: z.string().uuid(),
  sortOrder: z.number().int().min(0).max(9999),
  status: z.enum(["entwurf", "veroeffentlicht"]),
});

export const saveKeynoteSpeaker = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    speakerInputSchema.extend({ id: z.string().uuid().optional() }).parse(data),
  )
  .handler(async ({ data, context }) => {
    const payload = {
      profile_id: data.profileId,
      sort_order: data.sortOrder,
      status: data.status,
    };
    const query = data.id
      ? context.supabase.from("keynote_speakers").update(payload).eq("id", data.id)
      : context.supabase.from("keynote_speakers").insert(payload);
    const { error } = await query;
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

export const deleteKeynoteSpeaker = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("keynote_speakers")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

const topicInputSchema = z.object({
  id: z.string().uuid().optional(),
  speakerId: z.string().uuid(),
  heading: z.string().trim().min(1, "Bitte Überschrift angeben").max(200),
  bodyMd: z.string().trim().max(4000),
  sortOrder: z.number().int().min(0).max(9999),
  status: z.enum(["entwurf", "veroeffentlicht"]),
});

export const saveKeynoteTopic = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => topicInputSchema.parse(data))
  .handler(async ({ data, context }) => {
    const payload = {
      speaker_id: data.speakerId,
      heading: data.heading,
      body_md: data.bodyMd,
      sort_order: data.sortOrder,
      status: data.status,
    };
    const query = data.id
      ? context.supabase.from("keynote_topics").update(payload).eq("id", data.id)
      : context.supabase.from("keynote_topics").insert(payload);
    const { error } = await query;
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

export const deleteKeynoteTopic = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("keynote_topics").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });
